import React, { Fragment, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import Button from "react-bootstrap/Button";
import Table from "react-bootstrap/Table";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Badge from "react-bootstrap/Badge";
import { BsPencil, BsTrash, BsSend, BsPersonPlus } from "react-icons/bs";
import AxisSpinner from "./AxisSpinner";
import { AxisDateTimeFull } from "../util/AxisDateTime";
import { getStatusBadge } from "../util/RefereeStatusUtils";
import RefereeCreateEditModal from "../modals/RefereeCreateEditModal";
import { UserRefereeDetailModal } from "../modals/UserRefereeDetailModal";
import { UserLink } from "../util/Links";

const UserReferences = ({ user }) => {
  const { authAxios } = useAuth();
  const [loading, setLoading] = useState(false);
  const [references, setReferences] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedReferee, setSelectedReferee] = useState(null);

  useEffect(() => {
    if (user?.id) {
      fetchReferences();
    }
  }, [user?.id]);

  const fetchReferences = async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      const config = {
        headers: { "content-type": "application/json" },
        params: { user_id: user.id },
      };

      const response = await authAxios().get(
        "/api/backoffice/users/references",
        config
      );

      setReferences(response.data.content || []);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("Error fetching references:", error);
    }
  };

  const handleCreateReferee = () => {
    setSelectedReferee(null);
    setShowCreateModal(true);
  };

  const handleViewRefereeDetail = (referee) => {
    setSelectedReferee(referee);
    setShowDetailModal(true);
  };

  const handleEditReferee = (referee, event) => {
    event.stopPropagation();
    setSelectedReferee(referee);
    setShowEditModal(true);
  };

  const handleDeleteReferee = async (referee, event) => {
    event.stopPropagation();
    
    if (!window.confirm(`Are you sure you want to delete the reference for ${referee.refereeName}?`)) {
      return;
    }

    try {
      setLoading(true);
      const config = {
        headers: { "content-type": "application/json" },
        params: { 
          user_id: user.id,
          referee_id: referee.id 
        },
      };

      await authAxios().delete("/api/backoffice/users/references/delete", config);
      await fetchReferences();
    } catch (error) {
      setLoading(false);
      console.log("Error deleting referee:", error);
      alert("Failed to delete referee. Please try again.");
    }
  };

  const handleSendRefereeRequest = async (referee, event) => {
    event.stopPropagation();
    
    if (!window.confirm(`Send referee request to ${referee.refereeName} at ${referee.refereeEmail}?`)) {
      return;
    }

    try {
      setLoading(true);
      const config = {
        headers: { "content-type": "application/json" },
        params: { 
          user_id: user.id,
          referee_id: referee.id 
        },
      };

      await authAxios().post("/api/backoffice/users/references/send", null, config);
      await fetchReferences();
    } catch (error) {
      setLoading(false);
      console.log("Error sending referee request:", error);
      alert("Failed to send referee request. Please try again.");
    }
  };


  const canEdit = (referee) => referee.status === "created";
  const canDelete = (referee) => referee.status === "created";
  const canSend = (referee) => referee.status === "created";

  const SpinnerOrData = (loading) => {
    if (loading) return <AxisSpinner loading={loading} />;

    return (
      <Fragment>
        <Table striped bordered hover>
          <thead>
            <tr>
              <th>NAME</th>
              <th>EMAIL</th>
              <th>STATUS</th>
              <th>CONTACTED</th>
              <th>CONTACTED BY</th>
              <th>LAST EVENT</th>
              <th>REJECTION REASON</th>
              <th>PDF STORAGE</th>
              <th>CREATED</th>
            </tr>
          </thead>
          <tbody>
            {references.map((referee) => (
              <tr
                key={referee.id}
                onClick={() => handleViewRefereeDetail(referee)}
                style={{ cursor: 'pointer' }}
              >
                <td>
                  <strong>{referee.refereeName}</strong>
                </td>
                <td>{referee.refereeEmail}</td>
                <td>{getStatusBadge(referee.status)}</td>
                <td>
                  {referee.refereeContacted ? (
                    <Badge bg="success">Yes</Badge>
                  ) : (
                    <Badge bg="secondary">No</Badge>
                  )}
                </td>
                <td>
                  {referee.refereeContactedByUser ? (
                    UserLink(referee.refereeContactedByUser)
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </td>
                <td>
                  <div>
                    <small className="text-muted">{referee.lastEvent || '-'}</small>
                    {referee.lastEventAt && (
                      <div>
                        <small className="text-muted">
                          {AxisDateTimeFull(referee.lastEventAt)}
                        </small>
                      </div>
                    )}
                  </div>
                </td>
                <td>
                  {referee.rejectionReason ? (
                    <small className="text-danger">{referee.rejectionReason}</small>
                  ) : (
                    <small className="text-muted">-</small>
                  )}
                </td>
                <td>
                  {referee.pdfStorage ? (
                    <small className="font-monospace">{referee.pdfStorage}</small>
                  ) : (
                    <small className="text-muted">-</small>
                  )}
                </td>
                <td>
                  <small>{AxisDateTimeFull(referee.created)}</small>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Fragment>
    );
  };

  return (
    <Fragment>
      <Row className="icon-text-wrapper mb-3">
        <Col></Col>
        <Col>
          <Container>
            <Row xs="auto" className="justify-content-end">
              <Button
                variant="offblack"
                onClick={handleCreateReferee}
                disabled={loading}
              >
                + New Reference
              </Button>
            </Row>
          </Container>
        </Col>
      </Row>

      {SpinnerOrData(loading)}

      <RefereeCreateEditModal
        visible={showCreateModal}
        close={() => setShowCreateModal(false)}
        user={user}
        referee={null}
        onSuccess={() => {
          fetchReferences();
          setShowCreateModal(false);
        }}
      />

      <RefereeCreateEditModal
        visible={showEditModal}
        close={() => setShowEditModal(false)}
        user={user}
        referee={selectedReferee}
        onSuccess={() => {
          fetchReferences();
          setShowEditModal(false);
        }}
      />

      <UserRefereeDetailModal
        visible={showDetailModal}
        close={() => setShowDetailModal(false)}
        referee={selectedReferee}
        user={user}
        onRefereeUpdate={() => {
          fetchReferences(); // Refresh the table data
        }}
      />
    </Fragment>
  );
};

export default UserReferences;